import React, { Dispatch, SetStateAction, useContext, useEffect, useState } from "react"
import tw, { css, styled } from "twin.macro"
import ProfileHeader from "../profileHeader"
import { Questions, Answers } from "./questions"
import { CreateQuestionMutationFn, ProfileOnGetProfileFragment, ProfileQuery } from "../../graphql/generated/graphql"
import { createGlobalStyle } from "styled-components"
import { AskQuestion } from "../askQuestion"
import { UserSidebar } from "./userSidebar"
import { useTranslation } from "next-i18next"
import { isMobile } from "react-device-detect"
import { RedirectContext } from "../../utils/context"
import { FanSummary } from "../../components/fan"

interface ActiveTabProps {
  isActive: boolean
}
export const Tab = styled.div(({ isActive }: ActiveTabProps) => [
  tw`pb-2 text-base-2 cursor-pointer font-sans font-bold relative hover:(text-dark-purple)`,
  tw`after:(absolute -bottom-2 -right-5 -left-5 bg-base-2 h-1 rounded-full)`,
  isActive && tw`text-dark-purple after:(bg-dark-purple z-10)`,
])

export enum TabState {
  Questions = "questions",
  Answers = "answers",
  Fans = "fans",
}
interface TabProps {
  activeTab: TabState
  setActiveTab: Dispatch<SetStateAction<TabState>>
  isUser: boolean
}

const Tabs = (props: TabProps) => {
  const { activeTab, setActiveTab, isUser } = props
  const { t } = useTranslation("profile")

  return (
    <div tw="flex space-x-8 justify-center pb-4">
      <Tab
        isActive={activeTab === TabState.Answers}
        onClick={() => setActiveTab(TabState.Answers)}
        data-testid="tab-answer"
      >
        {t("answers.tab")}
      </Tab>
      <Tab
        isActive={activeTab === TabState.Questions}
        onClick={() => setActiveTab(TabState.Questions)}
        data-testid="tab-question"
      >
        {t("questions.tab")}
      </Tab>
      {isUser && !isMobile && (
        <Tab isActive={activeTab === TabState.Fans} onClick={() => setActiveTab(TabState.Fans)} data-testid="tab-fans">
          {t("fans.tab")}
        </Tab>
      )}
    </div>
  )
}

export interface ProfileProps {
  profileData: ProfileQuery
  profile: ProfileOnGetProfileFragment
  createQuestion: CreateQuestionMutationFn
  refetchProfileData: () => Promise<Record<string, any>>
}

const MainStyles = createGlobalStyle`
  ${css`
    main {
      ${tw`flex flex-col relative`}
    }
  `}, 
`
const ProfileComponent = (props: ProfileProps) => {
  const { getData } = useContext(RedirectContext)
  const redirectData = getData().data
  const { profileData, profile, refetchProfileData } = props
  const { User } = profileData
  const { id, isTakingQuestions, username } = profile
  const isUser = User?.id === id
  const hasSidebar = isTakingQuestions || isUser
  const hasFans = !!User && User.hasFans
  const [activeTab, setActiveTab] = useState<TabState>(isUser && hasFans ? TabState.Questions : TabState.Answers)

  const sidebar = (
    <>
      {isTakingQuestions && !isUser && <AskQuestion {...props} />}
      {isUser && !!User && <UserSidebar {...{ user: User, profile, refetchProfileData }} />}
    </>
  )
  useEffect(() => {
    const { activeTab } = redirectData
    if (activeTab) {
      let tab = !isUser && activeTab === TabState.Fans ? TabState.Answers : activeTab
      if (isMobile && tab === TabState.Fans) {
        tab = TabState.Answers
      }
      setActiveTab(tab)
    }
  }, [redirectData])

  const TabView = () => {
    switch (activeTab) {
      case TabState.Answers:
        return <Answers {...{ isUser, username }} />
      case TabState.Questions:
        return <Questions {...{ isUser, username }} />
      case TabState.Fans:
      default:
        return isUser && hasFans ? <FanSummary /> : <></>
    }
  }

  return (
    <>
      <MainStyles />
      <div
        tw="min-h-[800px] w-full max-w-screen-desktop-lg mx-auto px-4 py-4 tablet-lg:(py-10)"
        css={[hasSidebar && tw`flex`]}
      >
        <div
          tw="pr-0 space-y-5 tablet-lg:(space-y-8 w-2/3 mx-auto)"
          css={[
            hasSidebar && tw`tablet-lg:(pr-10 mx-0)`,
            css`
              flex: 2 1 auto;
            `,
          ]}
        >
          <ProfileHeader {...{ profile }} />
          {isMobile && <div data-testid="floating-sidebar">{sidebar}</div>}
          <Tabs activeTab={activeTab} setActiveTab={setActiveTab} isUser={isUser} />
          <TabView />
        </div>
        {!isMobile && <div data-testid="floating-sidebar">{sidebar}</div>}
      </div>
    </>
  )
}

export default ProfileComponent
